import React from 'react';
import PropTypes from 'prop-types';
import { alpha, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import PublicIcon from '@material-ui/icons/Public';
import GlobeIllustration from 'svg/illustrations/Globe';

const DEFAULT_TITLE = 'Comunidad Global';
const DEFAULT_DESCRIPTION =
  'Consolidamos tu identidad de marca ante una audiencia internacional mediante desarrollos digitales innovadores.';

const REGIONS = [
  { name: 'Venezuela', percentage: 51, accent: '#00acc1', highlight: true },
  { name: 'Estados Unidos', percentage: 22, accent: '#1565c0' },
  { name: 'España', percentage: 4, accent: '#c2185b' },
  { name: 'Otros', percentage: 23, accent: '#00838f' },
];

function RegionRow({ region }) {
  const { name, percentage, accent, highlight } = region;
  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: 2 },
        borderRadius: 2.5,
        bgcolor: 'background.paper',
        border: `1px solid ${alpha(accent, highlight ? 0.35 : 0.18)}`,
        boxShadow: highlight ? `0 10px 28px ${alpha(accent, 0.14)}` : `0 4px 16px ${alpha(accent, 0.06)}`,
        background: highlight
          ? `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, transparent 70%)`
          : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: accent,
              boxShadow: `0 0 0 3px ${alpha(accent, 0.25)}`,
            }}
          />
          <Typography sx={{ fontWeight: highlight ? 800 : 700, fontSize: '0.95rem', lineHeight: 1.3 }}>
            {name}
          </Typography>
          {highlight ? (
            <Typography
              component="span"
              sx={{
                ml: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                fontSize: '0.68rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                bgcolor: alpha(accent, 0.18),
                color: accent,
                whiteSpace: 'nowrap',
              }}
            >
              Principal
            </Typography>
          ) : null}
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: accent, flexShrink: 0 }}>
          {percentage}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: alpha(accent, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            bgcolor: accent,
          },
        }}
      />
    </Box>
  );
}

RegionRow.propTypes = {
  region: PropTypes.shape({
    name: PropTypes.string.isRequired,
    percentage: PropTypes.number.isRequired,
    accent: PropTypes.string.isRequired,
    highlight: PropTypes.bool,
  }).isRequired,
};

const GlobalCommunityMap = ({ content = {} }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const title = content.title || DEFAULT_TITLE;
  const description = content.description || DEFAULT_DESCRIPTION;

  return (
    <Box
      sx={{
        borderRadius: 4,
        overflow: 'hidden',
        background: `linear-gradient(165deg, ${alpha(secondary, 0.08)} 0%, ${alpha(primary, 0.05)} 55%, transparent 100%)`,
        px: { xs: 1, md: 2 },
        py: { xs: 3, md: 5 },
        my: { xs: 2, md: 3 },
      }}
    >
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={5} lg={5}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 260, md: 320 },
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              bgcolor: 'background.paper',
              border: `1px solid ${alpha(primary, 0.12)}`,
              boxShadow: `0 16px 48px ${alpha(primary, 0.1)}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '75%',
                aspectRatio: '1',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(secondary, 0.2)} 0%, transparent 70%)`,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
            <Box sx={{ position: 'relative', width: '100%', maxWidth: 420 }}>
              <GlobeIllustration width="100%" height="auto" />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                right: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.92)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(primary, 0.1)}`,
              }}
            >
              <PublicIcon sx={{ color: secondary, fontSize: 22 }} />
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', lineHeight: 1.35, color: primary }}>
                Audiencia distribuida en América, Europa y más.
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={7} lg={7}>
          <Typography
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.78rem' }}
            color="secondary"
            gutterBottom
          >
            {content.overline || 'Alcance internacional'}
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              letterSpacing: '-0.02em',
              color: 'primary.main',
              mb: 1.5,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '1.02rem', lineHeight: 1.7, mb: 3, maxWidth: 560 }}>
            {description}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {REGIONS.map((region) => (
              <RegionRow key={region.name} region={region} />
            ))}
          </Box>

          <Typography
            sx={{
              mt: 2.5,
              fontSize: '0.82rem',
              color: 'text.secondary',
              fontStyle: 'italic',
              lineHeight: 1.5,
            }}
          >
            Distribución estimada de audiencia digital del ecosistema TNO Network.
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

GlobalCommunityMap.propTypes = {
  content: PropTypes.shape({
    overline: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

GlobalCommunityMap.defaultProps = {
  content: {},
};

export default GlobalCommunityMap;
